import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import type {
  FieldType,
  OperatorId,
  RelativeDateValue,
} from '@/components/map/layer-filter-panel/operators';

/**
 * A single column filter row: a field, an operator matched to the field's
 * type, and a typed value whose shape is dictated by the operator's valueKind.
 * One filter per column — multiple rows AND-combine. `value` is omitted for
 * blank/not-blank operators (valueKind 'none').
 *
 * Value shapes by valueKind:
 *  - text:         { text: string }
 *  - number:       { number: number }
 *  - numberRange:  { min?: number; max?: number }
 *  - date:         { date: string }                 // ISO YYYY-MM-DD
 *  - dateRange:    { start?: string; end?: string }  // ISO YYYY-MM-DD
 *  - relativeDate: { relative: { n; unit } }
 *  - valueList:    { values: Set<string> }           // Set for efficient toggle
 *  - none:         undefined
 */
export type ColumnFilterValue =
  | { text: string }
  | { number: number }
  | { min?: number; max?: number }
  | { date: string }
  | { start?: string; end?: string }
  | { relative: RelativeDateValue }
  | { values: Set<string> };

export type ColumnFilter = {
  columnName: string;
  fieldType: FieldType;
  operator: OperatorId;
  value?: ColumnFilterValue;
};

export type LayerFiltersByColumn = Record<string, ColumnFilter>;

export type LayerFiltersState = {
  filtersPanelOpen: boolean;
  currentFilterLayerId: string | null;
  activeFilters: Record<string, LayerFiltersByColumn>;
  spatialFilters: Record<string, GeoJSON.Polygon>;
  setActiveFilters: (filters: Record<string, LayerFiltersByColumn>) => void;
  setSpatialFilters: (filters: Record<string, GeoJSON.Polygon>) => void;
  openFiltersPanel: (layerId: string) => void;
  closeFiltersPanel: () => void;
  setColumnFilter: (layerId: string, columnName: string, filter: ColumnFilter) => void;
  clearColumnFilter: (layerId: string, columnName: string) => void;
  resetFilterState: () => void;
};

const initialLayerFiltersState = {
  filtersPanelOpen: false,
  currentFilterLayerId: null,
  activeFilters: {},
  spatialFilters: {},
};

export const createLayerFiltersSlice = (set: StoreApi<any>['setState']) => ({
  ...initialLayerFiltersState,
  setActiveFilters: (filters: Record<string, LayerFiltersByColumn>) =>
    set(
      produce((draft: LayerFiltersState) => {
        draft.activeFilters = filters;
      })
    ),
  setSpatialFilters: (filters: Record<string, GeoJSON.Polygon>) =>
    set(
      produce((draft: LayerFiltersState) => {
        draft.spatialFilters = filters;
      })
    ),
  openFiltersPanel: (layerId: string) =>
    set(
      produce((draft: LayerFiltersState) => {
        draft.filtersPanelOpen = true;
        draft.currentFilterLayerId = layerId;
      })
    ),
  closeFiltersPanel: () =>
    set(
      produce((draft: LayerFiltersState) => {
        draft.filtersPanelOpen = false;
        draft.currentFilterLayerId = null;
      })
    ),
  setColumnFilter: (layerId: string, columnName: string, filter: ColumnFilter) =>
    set(
      produce((draft: LayerFiltersState) => {
        const existing = draft.activeFilters[layerId] || {};
        existing[columnName] = filter;
        draft.activeFilters[layerId] = existing;
      })
    ),
  clearColumnFilter: (layerId: string, columnName: string) =>
    set(
      produce((draft: LayerFiltersState) => {
        const existing = draft.activeFilters[layerId];
        if (!existing) return;

        delete existing[columnName];
        if (Object.keys(existing).length === 0) {
          delete draft.activeFilters[layerId];
        }
      })
    ),
  resetFilterState: () =>
    set(
      produce((draft: LayerFiltersState) => {
        draft.filtersPanelOpen = false;
        draft.currentFilterLayerId = null;
        draft.activeFilters = {};
        draft.spatialFilters = {};
      })
    ),
});
